import {
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    Typography,
} from "@mui/material";
import React, { ReactNode } from "react";
import AcxButton from "./AcxButton";
import { Close } from "@mui/icons-material";
import AcxMainTextField from "./AcxMainTextField";

interface AcxConfirmDeleteDialogProps {
    isOpen: boolean;
    /**
     * Callback that is executed when the open state of the dialog changes.
     */
    onOpenChange: (open: boolean) => void;
    /**
     * Callback that is executed when the delete action is confirmed.
     */
    onDelete?: (() => void) | (() => Promise<void>);
    /**
     * Callback that is executed when the cancel action is confirmed.
     */
    onCancel?: () => void;
    /**
     * The text that is required to be entered to confirm the delete action.
     */
    requiredText: string;
    children?: ReactNode;
}

/**
 * A delete dialog that requires the user to input certain text to confirm the delete action.
 */
export function AcxConfirmDeleteDialog(props: AcxConfirmDeleteDialogProps) {
    const [inputText, setInputText] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);

    const isSatisfied = inputText === props.requiredText;

    return (
        <Dialog
            open={props.isOpen}
            onClose={() => props.onOpenChange(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogContent>
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    paddingBottom={2}
                >
                    <Grid item>
                        <Typography
                            sx={(theme) => ({
                                color: theme.palette.gray[700],
                                fontSize: "14px",
                                fontWeight: "bold",
                            })}
                        >
                            Are you sure?
                        </Typography>
                    </Grid>
                    <Grid item>
                        <IconButton
                            size="small"
                            onClick={() => props.onOpenChange(false)}
                        >
                            <Close />
                        </IconButton>
                    </Grid>
                </Grid>
                {props.children}
                <Grid container direction="column" rowGap={1}>
                    <Grid item>
                        <Typography
                            sx={(theme) => ({
                                color: theme.palette.error.main,
                                fontWeight: 600,
                            })}
                        >
                            This action cannot be undone!
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography>
                            To confirm enter <b>{props.requiredText}</b> in the
                            box below:
                        </Typography>
                    </Grid>
                    <Grid item>
                        <AcxMainTextField
                            id="confirm-delete-dialog-input"
                            autoComplete="off"
                            value={inputText}
                            onChange={(event) =>
                                setInputText(event.target.value)
                            }
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <AcxButton
                    onClick={() => props.onOpenChange(false)}
                    color="white"
                >
                    Cancel
                </AcxButton>
                <AcxButton
                    onClick={async () => {
                        if (isSatisfied) {
                            setIsLoading(true);
                            await props.onDelete?.();
                            props.onOpenChange(false);
                            setIsLoading(false);
                        }
                    }}
                    color="error"
                    disabled={!isSatisfied}
                    loading={isLoading}
                >
                    Delete
                </AcxButton>
            </DialogActions>
        </Dialog>
    );
}
